import { normalizeBadge, normalizeTypography, withCurrentCurves } from './config';
import type { TypographyBadgeConfig, TypographyConfig, TypographyDesignItem } from './types';

export type TypographyPresetCategory = 'layout' | 'style' | 'badge';

/** Apply only the part promised by the preset category. */
export function applyTypographyPreset(
  current: TypographyConfig,
  category: TypographyPresetCategory,
  raw: unknown,
  fallbackColor = '#111827',
): TypographyConfig {
  const preset = normalizeTypography(raw, fallbackColor);
  if (category === 'style') {
    return normalizeTypography({ ...current, appearance: preset.appearance, ...(preset.font ? { font: preset.font } : {}) }, fallbackColor);
  }
  if (category === 'layout') {
    return withCurrentCurves(normalizeTypography({ ...current, layout: preset.layout }, fallbackColor));
  }
  return withCurrentCurves(preset);
}

export function applyBadgePreset(raw: unknown): TypographyBadgeConfig {
  return withCurrentCurves(normalizeBadge(raw));
}

/** Return a core-text patch without mutating the item's normal text styling. */
export function removeTypographyPatch(): Record<string, unknown> {
  return {
    typography: undefined,
    typographyFontReady: undefined,
    typographyFrame: undefined,
    typographyScale: undefined,
    textCurve: 0,
    textCurveSpacing: 0,
    textCurveReverse: false,
  };
}

/**
 * Carry a core `color` edit (floating toolbar, Inspector swatches) into the solid fill Typography
 * Pro actually paints; without it the text kept its old colour. Gradient and outline-only fills
 * own their colours in the panel and are left alone.
 */
export function syncTypographyColorPatch(patch: Record<string, unknown>, item: TypographyDesignItem | null | undefined): Record<string, unknown> {
  if (!patch || !Object.prototype.hasOwnProperty.call(patch, 'color') || Object.prototype.hasOwnProperty.call(patch, 'typography')) return patch;
  if (!item?.typography || item.typographyBadge) return patch;
  const color = String(patch.color ?? '');
  if (!color) return patch;
  const config = normalizeTypography(item.typography, String(item.color ?? '#111827'));
  if (config.appearance.fill.type !== 'solid' || config.appearance.fill.color === color) return patch;
  return { ...patch, typography: { ...config, appearance: { ...config.appearance, fill: { ...config.appearance.fill, color } } } };
}
